import React, { useState, useEffect, memo } from "react";
import { instance2 } from "../utils/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../shadcn/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../shadcn/form";
import { Input } from "../shadcn/input";
import {
  AlertTriangleIcon,
  CheckCircle,
  ClockIcon,
  InfoIcon,
  Loader2,
} from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "../shadcn/card";
import { z } from "zod";
import { toast } from "sonner";
import { useTimer } from "src/lib/hooks";
import {
  cn,
  formatToDisplayWithSeparators,
  localDateString,
  parseInputToNumericString,
} from "src/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../shadcn/alert-dialog";
import { padTimeUnit } from "src/lib/utils";
import { useCallback } from "react";
import { useMemo } from "react";
import { OrderDetailsDisplayCard } from "./components/order-details-display-card";
import jsPDF from "jspdf";
import BgnIcon from "../img/logobgn250.png";
import { basecode } from "../mainscreen/pesananscreen/baserobotocode";
import { formatNumber } from "../numberingformat";
import { basecodeBold } from "../mainscreen/pesananscreen/baserobotoboldcode";

const FEEDBACK_WINDOW_HOURS = 3;
const DEFAULT_MENU_ITEM = "-";
const TOAST_LOADING_ID = "loading-feedback";

// ✅ Fix the schema
const formSchema = z.object({
  received_qty: z.string().min(1, "Jumlah diterima tidak boleh kosong."),
  received_by: z.string().min(1, "Nama penerima tidak boleh kosong."),
  received_time: z.string().min(1, "Waktu diterima tidak boleh kosong."),
  received_pos: z.string().min(1, "Jabatan penerima tidak boleh kosong."),
  received_phone: z
    .string()
    .min(1, "Nomor telepon tidak boleh kosong.")
    .regex(/^\d+$/, "Nomor telepon hanya boleh berisi angka"),
  delivery: z.string().optional(),
});

const initialMenuData = {
  karbohidrat: DEFAULT_MENU_ITEM,
  protein: DEFAULT_MENU_ITEM,
  sayuran: DEFAULT_MENU_ITEM,
  buah: DEFAULT_MENU_ITEM,
  minuman: DEFAULT_MENU_ITEM,
};

const useOrderFeedbackData = (id) => {
  const [orderData, setOrderData] = useState(null);
  const [menuData, setMenuData] = useState(initialMenuData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!id) {
      setError("Informasi mengenai ID pesanan tidak ditemukan.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setOrderData(null);
    setMenuData(initialMenuData);

    try {
      const resultResponse = await instance2.get(`/customers_result/id/${id}`);

      if (!resultResponse || resultResponse.data.length === 0) {
        setError("Kami tidak dapat menemukan pesanan yang Anda cari.");
        setIsLoading(false);
        return;
      }

      const fetchedOrderData = resultResponse.data[0];

      const receivedDate = new Date(fetchedOrderData.date);
      const [receivedHours, receivedMinutes] = fetchedOrderData.received_time
        ? fetchedOrderData.received_time
            .split(":")
            .map((timeStr) => Number(timeStr))
        : [0, 0];

      const feedbackDeadlineTimestamp = fetchedOrderData.received_time
        ? new Date(fetchedOrderData.date).setHours(
            receivedHours + FEEDBACK_WINDOW_HOURS,
            receivedMinutes,
            0,
            0,
          )
        : null;

      const formattedReceivedDate = receivedDate.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

      const formattedRcvTime = fetchedOrderData.received_time
        ? `${padTimeUnit(receivedHours)}.${padTimeUnit(receivedMinutes)}`
        : "";

      setOrderData({
        ...fetchedOrderData,
        hasSubmittedReport: fetchedOrderData.received_qty !== null,
        dateReceivedObj: receivedDate,
        formattedDate: formattedReceivedDate,
        feedbackDeadline: feedbackDeadlineTimestamp,
        formattedReceivedTime: formattedRcvTime,
      });

      if (fetchedOrderData.location_id) {
        const menuResponse = await instance2.get(
          `/menu_schedule/${fetchedOrderData.location_id}/${localDateString(receivedDate)}/${localDateString(receivedDate)}`,
        );
        setMenuData({
          karbohidrat: menuResponse.data[0]?.KARBOHIDRAT ?? DEFAULT_MENU_ITEM,
          protein: menuResponse.data[0]?.PROTEIN ?? DEFAULT_MENU_ITEM,
          sayuran: menuResponse.data[0]?.SAYURAN ?? DEFAULT_MENU_ITEM,
          buah: menuResponse.data[0]?.BUAH ?? DEFAULT_MENU_ITEM,
          minuman: menuResponse.data[0]?.MINUMAN ?? DEFAULT_MENU_ITEM,
        });
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Terjadi kesalahan saat mengambil data.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { orderData, menuData, isLoading, error, refetchData: fetchData };
};

const GeneralErrorDisplay = memo(({ message, onRetry, onNavigateHome }) => (
  <div className="flex flex-col items-center gap-4 justify-center min-h-screen mx-auto">
    <div className="flex flex-col items-center justify-center gap-2 text-center">
      <h1 className="text-3xl text-merah-hover font-semibold text-balance">
        Maaf! Terjadi Kesalahan
      </h1>
      <p className="text-gray-600 text-balance max-w-[80ch]">{message}</p>
    </div>
    <div className="flex xs:flex-row flex-col items-center gap-2 w-fit">
      <Button className="bg-card" variant="outline" onClick={onNavigateHome}>
        Menuju halaman utama
      </Button>
      <Button className="w-full" onClick={onRetry}>
        Coba lagi
      </Button>
    </div>
  </div>
));

const FeedbackTimeoutDialog = memo(
  ({
    isOpen,
    onCancel,
    onGoHome,
    isReviewDeadlineOver,
    isPastScheduledDateNoReport,
  }) => (
    <AlertDialog defaultOpen={isOpen} open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Tidak dapat mengisi{" "}
            {isReviewDeadlineOver ? "ulasan" : "laporan penerimaan"}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-justify">
            {isReviewDeadlineOver &&
              "Anda hanya dapat mengisi ulasan maksimal 3 jam setelah makanan diterima."}
            {isPastScheduledDateNoReport &&
              "Anda hanya dapat mengisi laporan penerimaan pada hari yang sama dengan jadwal pengiriman."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Tutup</AlertDialogCancel>
          <AlertDialogAction onClick={onGoHome}>
            Kembali ke halaman utama
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ),
);

const OrderStatusMessages = memo(
  ({ orderData, isPastScheduledDate, isReviewDeadlineOver }) => (
    <>
      {orderData?.hasSubmittedReport && (
        <Card className="bg-green-700/10 border-green-700/40">
          <div className="flex items-center gap-4 p-6">
            <CheckCircle className="hidden xs:block text-green-900 shrink-0" />
            <CardHeader className="space-y-0 p-0">
              <CardTitle className="text-sm xs:text-base text-green-900 text-balance">
                Diterima oleh {orderData.received_by}
              </CardTitle>
              <CardDescription className="text-green-800 text-xs text-balance">
                pada {orderData.formattedDate}, pukul{" "}
                {orderData.formattedReceivedTime} WIB.
              </CardDescription>
            </CardHeader>
          </div>
        </Card>
      )}
      {isPastScheduledDate && !orderData?.hasSubmittedReport && (
        <Card className="flex items-center gap-3 w-full py-2 px-4 bg-merah-hover/10 border-merah-hover/40 text-merah-no text-sm">
          <AlertTriangleIcon className="shrink-0" size={16} /> Anda tidak dapat
          mengisi laporan penerimaan karena telah melewati tanggal pesanan.
        </Card>
      )}
      {orderData?.hasSubmittedReport && isReviewDeadlineOver && (
        <Card className="flex items-center gap-3 w-full py-2 px-4 bg-blue-400/10 border-blue-500/40 text-blue-900 text-sm rounded-[0.75rem]">
          <InfoIcon className="shrink-0" size={16} /> Waktu untuk mengisi ulasan
          telah berakhir.
        </Card>
      )}
    </>
  ),
);

const ReceptionReportForm = memo(
  ({ form, onSubmit, isLoading, isDisabled }) => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 w-full">
        <div className="grid sm:grid-cols-3 grid-cols-1 gap-4 w-full">
          <FormField
            control={form.control}
            name="received_qty"
            render={({ field }) => (
              <FormItem className="w-full sm:col-span-2">
                <FormLabel>Jumlah diterima (Porsi)</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      disabled={isDisabled}
                      placeholder="Jumlah Porsi yang diterima"
                      type="text"
                      inputMode="numeric"
                      ref={field.ref}
                      name={field.name}
                      onBlur={field.onBlur}
                      value={formatToDisplayWithSeparators(field.value || "")}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const numericString =
                          parseInputToNumericString(inputValue);
                        field.onChange(numericString);
                      }}
                    />
                    <span className="absolute top-1/2 -translate-y-1/2 right-0 -translate-x-4 text-sm text-neutral-500">
                      Porsi
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="received_time"
            render={({ field }) => (
              <FormItem className="w-full sm:col-span-1">
                <FormLabel>Waktu diterima</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      disabled={isDisabled}
                      placeholder="Masukkan waktu makanan diterima"
                      type="time"
                      {...field}
                    />
                    <span className="absolute top-1/2 -translate-y-1/2 right-0 -translate-x-4 text-sm text-neutral-500">
                      WIB
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="received_by"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nama penerima</FormLabel>
              <FormControl>
                <Input
                  disabled={isDisabled}
                  placeholder="Masukkan nama penerima"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="received_phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nomor telepon penerima</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  autoComplete="tel"
                  disabled={isDisabled}
                  placeholder="Masukkan nomor telepon"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(e.target.value.replace(/\D/g, ""))
                  }
                  onKeyDown={(e) => {
                    if (
                      ["e", "E", "+", "-", ".", " ", "/", "\\"].includes(e.key)
                    )
                      e.preventDefault();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="received_pos"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Jabatan penerima</FormLabel>
              <FormControl>
                <Input
                  disabled={isDisabled}
                  placeholder="Masukkan jabatan penerima"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className="flex items-center gap-2 w-full"
          type="submit"
          disabled={isLoading || isDisabled}
        >
          {isLoading && <Loader2 className="animate-spin" />} Kirim
        </Button>
      </form>
    </Form>
  ),
);

const ReviewPromptSection = memo(
  ({ timeLeft, onNavigateToComplain, isDisabled }) => (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="flex items-center gap-2 text-neutral-600">
          <ClockIcon size={20} /> <span>Batas waktu mengisi ulasan</span>
        </div>
        <div
          className={cn(
            "grid w-full text gap-4",
            timeLeft["hari"] === "00" ? "grid-cols-3" : "grid-cols-4",
          )}
        >
          {Object.entries(timeLeft)
            .filter(([key]) =>
              timeLeft["hari"] === "00" ? key !== "hari" : true,
            )
            .map(([key, value]) => (
              <div key={`${key}-${value}`} className="flex flex-col gap-2">
                <span className="p-3 bg-neutral-200 text-neutral-600 rounded-md text-center">
                  {value}
                </span>
                <span className="text-xs text-center text-neutral-600 capitalize">
                  {key}
                </span>
              </div>
            ))}
        </div>
      </div>
      <Button
        className="flex items-center gap-2 w-full"
        type="button"
        disabled={isDisabled}
        onClick={onNavigateToComplain}
      >
        Beri ulasan
      </Button>
    </div>
  ),
);

export default function UserFeedback() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");

  const {
    orderData,
    menuData,
    isLoading: isLoadingData,
    error: dataError,
    refetchData,
  } = useOrderFeedbackData(id);

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);

  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      received_qty: "",
      received_by: "",
      received_time: "",
      received_pos: "",
      received_phone: "",
      delivery: "",
    },
  });

  const { timeLeft, timeIsOut: reviewDeadlineIsOver } = useTimer({
    deadline: orderData?.feedbackDeadline,
  });

  const isPastScheduledDate = useMemo(() => {
    if (!orderData?.dateReceivedObj) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const scheduledDate = new Date(orderData.dateReceivedObj);
    scheduledDate.setHours(0, 0, 0, 0);

    return today.getTime() > scheduledDate.getTime();
  }, [orderData?.dateReceivedObj]);

  const canSubmitReport = useMemo(
    () => !orderData?.hasSubmittedReport && !isPastScheduledDate,
    [orderData?.hasSubmittedReport, isPastScheduledDate],
  );

  const canSubmitReview = useMemo(
    () => orderData?.hasSubmittedReport && !reviewDeadlineIsOver,
    [orderData?.hasSubmittedReport, reviewDeadlineIsOver],
  );

  const isReceptionFormDisabled =
    !canSubmitReport || orderData?.hasSubmittedReport;

  const shouldShowTimeoutModal = useMemo(() => {
    if (orderData?.hasSubmittedReport) return reviewDeadlineIsOver;
    return isPastScheduledDate || reviewDeadlineIsOver;
  }, [
    orderData?.hasSubmittedReport,
    isPastScheduledDate,
    reviewDeadlineIsOver,
  ]);

  useEffect(() => {
    if (!isLoadingData && orderData && shouldShowTimeoutModal) {
      if (
        (isPastScheduledDate && !orderData.hasSubmittedReport) ||
        (reviewDeadlineIsOver && orderData.hasSubmittedReport)
      ) {
        setShowTimeoutDialog(true);
      }
    } else {
      setShowTimeoutDialog(false);
    }
  }, [
    isLoadingData,
    orderData,
    shouldShowTimeoutModal,
    isPastScheduledDate,
    reviewDeadlineIsOver,
  ]);

  const handleSubmitReport = async (formDataValues) => {
    setIsSubmittingForm(true);
    toast.loading("Menambahkan laporan...", { id: TOAST_LOADING_ID });

    const submissionData = new FormData();
    submissionData.append("id", id);
    submissionData.append("received_qty", formDataValues.received_qty);
    submissionData.append("received_time", formDataValues.received_time);
    submissionData.append("received_by", formDataValues.received_by);
    submissionData.append("received_phone", formDataValues.received_phone);
    submissionData.append("received_pos", formDataValues.received_pos);
    submissionData.append("delivery", formDataValues.delivery);

    try {
      const response = await instance2.post(
        "/customers_result/received",
        submissionData,
      );
      if (response.status === "OK") {
        toast.dismiss(TOAST_LOADING_ID);
        toast.success("Berhasil menambahkan laporan!");
        refetchData();
        form.reset();
      } else {
        toast.dismiss(TOAST_LOADING_ID);
        toast.error(response.data?.message || "Gagal menambahkan laporan.");
      }
    } catch (err) {
      toast.dismiss(TOAST_LOADING_ID);
      toast.error(
        `Terjadi kesalahan: ${err.message || "Tidak dapat terhubung ke server."}`,
      );
    } finally {
      setIsSubmittingForm(false);
    }
  };

  if (isLoadingData && !dataError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-merah-hover" />
      </div>
    );
  }

  if (dataError) {
    return <GeneralErrorDisplay message={dataError} onRetry={refetchData} />;
  }

  if (!orderData && !isLoadingData) {
    return (
      <GeneralErrorDisplay
        message="Tidak dapat memuat detail pesanan."
        onRetry={refetchData}
      />
    );
  }
  // Category -> menu key used in your table
  const CATEGORY_ID_BY_KEY = {
    karbohidrat: 1,
    protein: 2,
    sayuran: 3,
    buah: 4,
    minuman: 5,
  };

  // Returns: { karbohidrat: {1:val,2:val,3:val,4:val,5:val}, protein: {...}, ... }
  const fetchRatingsForResult = async (resultId) => {
    try {
      const res = await instance2.get("/customers/result_rate_detail", {
        params: { param: "result_id", id: resultId },
      });
      let rows = Array.isArray(res?.data) ? res.data : [];
      // stable ordering so "last wins" is deterministic
      rows = rows.sort((a, b) => a.id - b.id);

      // Flip category_id -> menuKey we use in PDF
      const catToKey = Object.fromEntries(
        Object.entries(CATEGORY_ID_BY_KEY).map(([k, cid]) => [cid, k]),
      );

      const byMenu = {}; // { [menuKey]: { [param_id]: value } }
      for (const r of rows) {
        const menuKey = catToKey[r.category_id];
        if (!menuKey) continue;
        if (!byMenu[menuKey]) byMenu[menuKey] = {};
        byMenu[menuKey][r.param_id] = r.value; // last wins
      }
      return byMenu;
    } catch (e) {
      console.error("Failed to fetch result_rate_detail:", e);
      return {};
    }
  };

  // Turn a Blob into a data URL (base64) that jsPDF can embed
  const blobToDataURL = (blob) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });

  // Robustly fetch feedback images for a result and return data URLs
  const fetchFeedbackImageDataURLs = async (resultId) => {
    // 1) Get rows that have an image reference (image_id or image path)
    const res = await instance2.get(`/customers_review/feedback/${resultId}`);
    const rows = (res?.data?.data ?? res?.data ?? []).filter(
      (r) => !!r?.image_id || !!r?.image,
    );

    if (!rows.length) return [];

    // 2) Fetch each image as Blob; guard against interceptors that unwrap to Blob directly
    const blobs = await Promise.all(
      rows.map(async (row) => {
        const url = row.image_id ? `/api/image/${row.image_id}` : row.image;
        const r = await instance2.get(url, {
          responseType: "blob",
          transformResponse: [(d) => d], // keep raw
        });
        return r?.data ?? r; // if interceptor already returned Blob
      }),
    );

    // 3) Convert to data URLs
    return Promise.all(blobs.map((b) => blobToDataURL(b)));
  };

  // Get intrinsic width/height for a dataURL (for aspect-ratio aware sizing)
  const getImageDims = (dataUrl) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.width, h: img.height });
      img.onerror = () => resolve({ w: 1000, h: 1000 }); // fallback
      img.src = dataUrl;
    });

  const onClickDownloadPDF = async () => {
    if (isPdfDownloading) return; // guard double-clicks
    setIsPdfDownloading(true);
    try {
      await handleDownloadPagePDF(orderData, menuData);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const handleDownloadPagePDF = async (orderData, menuData) => {
    const doc = new jsPDF();
    const dateStr = orderData.formattedDate || "N/A";
    const kitchenName = localStorage.getItem("kitchenname") || "Dapur";
    const ratingsByMenu = await fetchRatingsForResult(id);

    // Embed Roboto font
    doc.addFileToVFS("Roboto-Regular.ttf", basecode);
    doc.addFileToVFS("Roboto-Bold.ttf", basecodeBold);
    doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
    doc.setFont("Roboto", "normal");

    // Load icon as base64
    const loadImageAsBase64 = (imgSrc) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = imgSrc;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (err) => reject(err);
      });

    const iconBase64 = await loadImageAsBase64(BgnIcon);
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const drawFauxBoldLeft = (
      text,
      centerX,
      y,
      repeats = 1.5,
      offset = 0.1,
    ) => {
      doc.setFont("Roboto", "normal");
      doc.setFontSize(10);
      for (let i = 0; i < repeats; i++) {
        const dx = (i % 2 ? 1 : -1) * offset;
        doc.text(text, centerX + dx, y, { align: "left" });
      }
    };

    // Header function
    // Header function (centered title & address; blue line under header)
    const addHeader = (doc, pageWidth, orderDate, kitchenName) => {
      const textYOffset = 10;
      const centerX = pageWidth / 2;
      const marginX = 14;

      // Icon on the LEFT
      const iconWidth = 30;
      const iconHeight = 30;
      const iconX = 14;
      const iconY = textYOffset + 2;
      doc.addImage(iconBase64, "PNG", iconX, iconY, iconWidth, iconHeight);

      const drawFauxBoldCentered = (
        text,
        centerX,
        y,
        repeats = 1.5,
        offset = 0.1,
      ) => {
        doc.setFont("Roboto", "normal");
        doc.setFontSize(10);
        for (let i = 0; i < repeats; i++) {
          const dx = (i % 2 ? 1 : -1) * offset;
          doc.text(text, centerX + dx, y, { align: "center" });
        }
      };
      // Title (CENTERED)
      doc.setFontSize(12);
      doc.setFont("Roboto", "bold");
      doc.setTextColor(59, 78, 135);
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(10);
      doc.setFont("Roboto", "normal");

      // Address (CENTERED)
      const alamatLines = [
        "BADAN GIZI NASIONAL",
        "SATUAN PELAYANAN PEMENUHAN GIZI (SPPG)",
        "KECAMATAN ARCAMANIK",
      ];

      const startYBase = textYOffset + iconHeight / 2 - 4;
      let addrY = startYBase;

      doc.setFontSize(8);
      alamatLines.forEach((line) => {
        drawFauxBoldCentered(line, centerX, addrY);
        addrY += 5;
      });
      const s =
        "Jl. A.H. Nasution No. 114, Kel Cisaranten Bina Harapan, Kec. Arcamanik";
      const w = doc.getTextWidth(s);
      doc.text(s, centerX - w / 2, addrY);

      // === Horizontal blue line under the header (full width within margins)
      const lineY = addrY + 10; // a little space below the link
      doc.setLineWidth(2);
      doc.setDrawColor(100, 149, 237);
      doc.line(marginX, lineY, pageWidth - marginX, lineY);

      // Reset colors
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);

      const underlineText = "EVALUASI HARIAN MAKAN BERGIZI GRATIS";

      // make sure font/size are set BEFORE measuring
      doc.setFont("Roboto", "bold");
      doc.setFontSize(10);

      const textY = lineY + 10;

      // usage
      drawFauxBoldCentered(underlineText, centerX, textY);

      // underline still based on measured width
      const textWidth = doc.getTextWidth(underlineText);
      const underlineY = textY + 2; // tweak offset if needed

      doc.setLineWidth(0.5);
      doc.line(
        centerX - textWidth / 2,
        underlineY,
        centerX + textWidth / 2,
        underlineY,
      );

      // Info section
      const rowHeight = 8;
      const colWidth = pageWidth - marginX * 2; // Full width for the Dapur section

      return lineY + 15;
    };

    // Add header to first page
    let yOffset = addHeader(doc, pageWidth, dateStr, kitchenName);

    // Add Menu Section
    doc.setFontSize(10);
    doc.setFont("Roboto", "bold");
    doc.setTextColor(59, 78, 135);
    yOffset += 3;

    // --- Details block (aligned colons) ---
    doc.setFontSize(10);

    // Safely compute day name from orderData (fallbacks to "-")
    const pickDate =
      orderData?.dateISO || orderData?.orderDate || orderData?.date || dateStr;
    let hariValue = "-";
    try {
      const d = new Date(pickDate);
      if (!isNaN(d))
        hariValue = d.toLocaleDateString("id-ID", { weekday: "long" });
    } catch (_) {}

    doc.setTextColor(0, 0, 0);

    const details = [
      ["Hari", hariValue],
      ["Tanggal", dateStr],
      ["Instansi", orderData?.customer_name || "Badan Gizi Nasional"],
      ["Sesi Makan", orderData?.delivery || ""],
      ["Nama Kader Penanggungjawab", orderData.received_by || ""],
    ];

    // Measure with the SAME font/size used for labels
    doc.setFont("Roboto", "bold");
    const labelWidths = details.map(([label]) => doc.getTextWidth(label));
    const maxLabelW = Math.max(...labelWidths);

    // Layout
    const startX = 14; // left margin
    let curY = yOffset; // start just below header
    const padLabelToColon = 4; // space between label end and ":"
    const padColonToValue = 6; // space from ":" to value
    const lineGap = 5; // vertical gap between rows

    // Draw rows with aligned colons
    for (const [label, value] of details) {
      const colonX = startX + maxLabelW + padLabelToColon;
      const valueX = colonX + padColonToValue;

      // label (bold)
      doc.setFont("Roboto", "bold");
      doc.text(label, startX, curY);

      // colon + value (normal)
      doc.setFont("Roboto", "normal");
      doc.text(":", colonX, curY);
      doc.text(String(value), valueX, curY);

      curY += lineGap;
    }

    // Move yOffset after the details block + a little spacing
    yOffset = curY + 6;
    doc.setFont("Roboto", "bold");
    doc.setTextColor(0, 0, 0);
    drawFauxBoldLeft("A. Daya Terima Sasaran", 14, yOffset);
    yOffset += 3;

    // Restore defaults for table
    doc.setFont("Roboto", "normal");
    doc.setTextColor(0, 0, 0);

    const qtyText =
      (orderData?.received_qty ?? null) !== null
        ? `${formatNumber(orderData.received_qty)} ompreng`
        : "-";

    // last column shows a. and b. stacked
    const menuItems1 = [
      [
        "1",
        qtyText,
        formatNumber(orderData.received_qty) || "",
        `a. Jumlah ompreng yang habis : ${orderData.omprenghabis ? formatNumber(orderData.omprenghabis) : "-"}\nb. Jumlah ompreng yang tersisa : ${orderData.omprengsisa ? formatNumber(orderData.omprengsisa) : "-"}`,
      ], // <-- last cell has two lines
    ];

    const head1 = [
      [
        { content: "No", styles: { valign: "middle", halign: "center" } },
        {
          content: "Jumlah Pengiriman",
          styles: { valign: "middle", halign: "center" },
        },
        {
          content: "Jumlah Diterima",
          styles: { valign: "middle", halign: "center" },
        },
        {
          content: "Keterangan",
          styles: { valign: "middle", halign: "center" },
        },
      ],
    ];

    doc.autoTable({
      theme: "grid",
      startY: yOffset,
      head: head1,
      body: menuItems1,
      styles: {
        fontSize: 10,
        font: "Roboto",
        lineWidth: 0.2, // <-- add
        lineColor: [0, 0, 0], // <-- add
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.2, // <-- add
        lineColor: [0, 0, 0], // <-- add
        // borderColor: ... // not a valid option
      },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
      },
      bodyStyles: { textColor: [0, 0, 0] }, // <-- BODY text color
      tableLineWidth: 0.2, // optional: outer table border
      tableLineColor: [0, 0, 0], // optional
    });

    yOffset = doc.lastAutoTable.finalY + 10;

    // (optional) Section title "MENU" centered or left — your call
    doc.setFont("Roboto", "bold");
    doc.setTextColor(0, 0, 0);
    drawFauxBoldLeft("B. Uji Cita Rasa (Oleh Kader)", 14, yOffset);
    yOffset += 3;

    // Restore defaults for table
    doc.setFont("Roboto", "normal");
    doc.setTextColor(0, 0, 0);

    const menuOrder = ["karbohidrat", "protein", "sayuran", "buah", "minuman"];
    const menuItems = menuOrder
      .filter((key) => menuData?.[key] !== "-")
      .map((key, idx) => {
        const vals = ratingsByMenu[key] || {};
        return [
          String(idx + 1),
          menuData?.[key] ?? "-",
          vals[1] ?? "", // Warna
          vals[2] ?? "", // Aroma
          vals[3] ?? "", // Tekstur
          vals[4] ?? "", // Rasa
          vals[5] ?? "", // Kematangan
        ];
      });

    const head = [
      [
        {
          content: "No",
          rowSpan: 2,
          styles: { valign: "middle", halign: "center" },
        },
        {
          content: "Nama Menu",
          rowSpan: 2,
          styles: { valign: "middle", halign: "center" },
        },
        {
          content: "Komponen Organoleptik",
          colSpan: 5,
          styles: { halign: "center" },
        },
      ],
      [
        { content: "Warna", styles: { halign: "center" } },
        { content: "Aroma", styles: { halign: "center" } },
        { content: "Tekstur", styles: { halign: "center" } },
        { content: "Rasa", styles: { halign: "center" } },
        { content: "Kematangan", styles: { halign: "center" } },
      ],
    ];

    doc.autoTable({
      theme: "grid",
      startY: yOffset,
      head,
      body: menuItems,
      styles: {
        fontSize: 10,
        font: "Roboto",
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "center" }, // No
        1: { halign: "left" }, // Nama Menu
        2: { halign: "center" }, // Warna
        3: { halign: "center" }, // Aroma
        4: { halign: "center" }, // Tekstur
        5: { halign: "center" }, // Rasa
        6: { halign: "center" }, // Kematangan
      },
      bodyStyles: { textColor: [0, 0, 0] },
      tableLineWidth: 0.2,
      tableLineColor: [0, 0, 0],
    });
    doc.setFontSize(9);
    doc.text(
      "*Keterangan: 1 = Sangat Tidak Suka, 2 = Tidak Suka, 3 = Netral, 4 = Suka, 5 = Sangat Suka",
      startX,
      doc.lastAutoTable.finalY + 5,
    );

    yOffset = doc.lastAutoTable.finalY + 10;

    doc.setFont("Roboto", "bold");
    doc.setTextColor(0, 0, 0);

    // Restore defaults for table
    doc.setFont("Roboto", "normal");
    doc.setTextColor(0, 0, 0);

    // last column shows a. and b. stacked
    const menuItems2 = [
      [orderData.feedback || ""], // <-- last cell has two lines
    ];

    const head2 = [
      [
        {
          content: "Saran Dan Masukan",
          styles: { valign: "middle", halign: "center" },
        },
      ],
    ];

    const BODY_HEIGHT = 40; // <- desired row height in points

    doc.autoTable({
      theme: "grid",
      startY: yOffset,
      head: head2,
      body: menuItems2,
      styles: {
        fontSize: 10,
        font: "Roboto",
        lineWidth: 0.2, // <-- add
        lineColor: [0, 0, 0], // <-- add
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.2, // <-- add
        lineColor: [0, 0, 0], // <-- add
        // borderColor: ... // not a valid option
      },
      columnStyles: {
        0: { halign: "left" },
      },
      bodyStyles: { textColor: [0, 0, 0], minCellHeight: BODY_HEIGHT }, // <-- BODY text color
      tableLineWidth: 0.2, // optional: outer table border
      tableLineColor: [0, 0, 0], // optional
    });

    yOffset = doc.lastAutoTable.finalY + 10;

    const renderSection = (title, items, startX, tableWidth) => {
      if (items.length === 0) return;

      doc.setFontSize(12);
      doc.setFont("Roboto", "bold");
      doc.text(title, startX, yOffset);

      const sectionTotal = items.reduce(
        (sum, item) => sum + Number(item.total_qty || 0),
        0,
      );

      const finalY = doc.lastAutoTable.finalY;

      doc.setFontSize(9);
      doc.setFont("Roboto", "bold");
      doc.text(`Total Porsi ${title}:`, startX, finalY + 6);
      doc.text(
        formatNumber(sectionTotal),
        startX + tableWidth - doc.getTextWidth(formatNumber(sectionTotal)),
        finalY + 6,
      );

      return finalY + 12;
    };

    // --- centered name above a signature line ---
    doc.setFont("Roboto", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const label = orderData?.received_by || "";
    const baseY = yOffset + 5; // text baseline
    const minLineW = 60; // minimum line length
    const pad = 10; // extra space beyond text width

    // size the line to the text width so centering is always perfect
    const labelW = doc.getTextWidth(label);
    const lineW = Math.max(minLineW, labelW + pad);

    // place the line near the right margin (same visual spot as before)
    const lineY = baseY + 20;
    const lineX1 = pageWidth - 50 - lineW / 2;
    const lineX2 = lineX1 + lineW;

    // midpoint of the line
    const xMid = (lineX1 + lineX2) / 2;

    // draw centered name above the line
    doc.text("Penerima", xMid, baseY, { align: "center" });

    // draw the line
    doc.line(lineX1, lineY, lineX2, lineY);

    doc.text(label, xMid, lineY + 8, { align: "center" });

    // === IMAGE GALLERY (2 images per page) ===
    try {
      const imageDataUrls = await fetchFeedbackImageDataURLs(id);

      if (imageDataUrls.length) {
        // Start on a fresh page after your tables/sections
        doc.addPage();

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const marginX = 14;
        const marginY = 14;
        const gapY = 8; // gap between the two images on the same page

        // Available drawing area
        const availW = pageW - marginX * 2;
        const availH = pageH - marginY * 2;

        // Each page is split into two vertical slots
        const slotH = (availH - gapY) / 2;

        // Optional: title for the gallery
        doc.setFont("Roboto", "bold");
        doc.setFontSize(12);
        doc.text("Lampiran Foto", marginX, marginY - 4);
        doc.setFont("Roboto", "normal");

        for (let i = 0; i < imageDataUrls.length; i++) {
          // New page every 2 images (but not before drawing the first)
          if (i > 0 && i % 2 === 0) {
            doc.addPage();
            // (re)draw the title per page if you want
            doc.setFont("Roboto", "bold");
            doc.setFontSize(12);
            doc.text("Lampiran Foto", marginX, marginY - 4);
            doc.setFont("Roboto", "normal");
          }

          const dataUrl = imageDataUrls[i];
          const fmt = dataUrl.startsWith("data:image/jpeg") ? "JPEG" : "PNG";

          // Which slot on the page? (0 = top, 1 = bottom)
          const slotIdx = i % 2;
          const slotTopY = marginY + slotIdx * (slotH + gapY);

          // Get intrinsic dimensions to preserve aspect ratio
          const { w: natW, h: natH } = await getImageDims(dataUrl);

          // Scale to fit within (availW x slotH), preserving aspect ratio
          const scale = Math.min(availW / natW, slotH / natH);
          const drawW = Math.max(1, Math.min(availW, natW * scale));
          const drawH = Math.max(1, Math.min(slotH, natH * scale));

          // Center horizontally within the slot
          const x = marginX + (availW - drawW) / 2;
          const y = slotTopY + (slotH - drawH) / 2;

          // Draw image
          doc.addImage(dataUrl, fmt, x, y, drawW, drawH);

          // Optional: caption under each image
          // doc.setFontSize(9);
          // doc.text(`Foto ${i + 1}`, marginX, y + drawH + 5);
        }
      }
    } catch (e) {
      console.error("Failed to append images to PDF:", e);
      // Continue saving PDF even if images fail
    }

    doc.save(`Evaluasi_Harian ${dateStr}.pdf`);
  };

  return (
    <div className="grid min-h-[100dvh] bg-background py-12">
      <FeedbackTimeoutDialog
        isOpen={showTimeoutDialog}
        onCancel={() => setShowTimeoutDialog(false)}
        onGoHome={() => navigate("/")}
        isReviewDeadlineOver={
          reviewDeadlineIsOver && orderData?.hasSubmittedReport
        }
        isPastScheduledDateNoReport={
          isPastScheduledDate && !orderData?.hasSubmittedReport
        }
      />

      <div className="flex flex-col justify-center items-center gap-5 w-full px-6 xs:px-12 max-w-xl mx-auto">
        <div className="flex flex-col gap-2 items-center">
          <h1 className="text-3xl font-semibold text-balance w-full text-center">
            Penerimaan Makanan
          </h1>
          <p className="font-light text-gray-500 text-sm text-pretty text-center">
            Informasi mengenai pengiriman dan penerimaan makanan
          </p>
        </div>

        <div className="flex flex-col w-full gap-3">
          <OrderStatusMessages
            orderData={orderData}
            isPastScheduledDate={isPastScheduledDate}
            isReviewDeadlineOver={reviewDeadlineIsOver}
          />
          <OrderDetailsDisplayCard orderData={orderData} menuData={menuData} />
        </div>

        {canSubmitReview && (
          <Button
            className="flex items-center gap-2 w-full mt-4"
            type="button"
            onClick={onClickDownloadPDF}
            disabled={isPdfDownloading}
          >
            {isPdfDownloading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Mengunduh…
              </>
            ) : (
              "Download PDF"
            )}
          </Button>
        )}
        {canSubmitReport && (
          <ReceptionReportForm
            form={form}
            onSubmit={handleSubmitReport}
            isLoading={isSubmittingForm}
            isDisabled={isReceptionFormDisabled || showTimeoutDialog}
          />
        )}

        {canSubmitReview && (
          <ReviewPromptSection
            timeLeft={timeLeft}
            onNavigateToComplain={() => navigate(`/usercomplain?id=${id}`)}
            isDisabled={
              isSubmittingForm || reviewDeadlineIsOver || showTimeoutDialog
            }
          />
        )}
      </div>
    </div>
  );
}
